import {
  DatabaseOutlined,
  FieldTimeOutlined,
  FileTextOutlined,
  LayoutOutlined,
  UserOutlined,
} from '@ant-design/icons'
import { Form } from 'antd'
import { t } from 'i18next'
import { useEffect, useState } from 'react'
import Moment from 'react-moment'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'
import {
  Badge,
  IconTextContainer,
  MultiColumnFormColumnContainer,
  MultiColumnFormContentContainer,
  ReadOnlyFormField,
  SectionLoadingState,
} from '../../../components'
import {
  BadgeColors,
  NO_DATA_PLACEHOLDER,
  dateFormat,
  workspaceDocumentSetStatuses,
} from '../../../constants/constants'
import { useDataSources, useUsers } from '../../../hooks'
import useWorkspaceDetails from '../../../hooks/useWorkspaceDetails'
import { User } from '../../../store/reducers/userReducer'

const SectionLoadingStateContainer = styled.div`
  display: flex;
  height: calc(100vh - 124px);
`

interface DocumentDetails {
  documentTitle?: string
  dataSource?: string
  status?: string
  lastUpdate?: string
  owner?: User
  latestIteration?: string
  ofrTemplateName?: string
}

const WorkspaceOverviewInfoConformityCheckDocumentPanel = () => {
  const { documentId } = useParams()
  const { selectedWorkspace } = useWorkspaceDetails({
    preventFetch: true,
  })

  const { getLatestIterationOfFile } = useDataSources({
    preventFetch: true,
  })

  const { findUser } = useUsers({})

  const [documentDetails, setDocumentDetails] = useState<DocumentDetails>()
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const fetchDocumentDetails = async () => {
      setIsLoading(true)
      try {
        if (!selectedWorkspace?.config?.conformityChecksConfig?.documents) {
          return
        }

        const latestIteration =
          (await getLatestIterationOfFile({
            dataSourceId: selectedWorkspace.dataSource?.id ?? '',
            documentId: documentId ?? '',
          })) || NO_DATA_PLACEHOLDER

        const document =
          selectedWorkspace.config.conformityChecksConfig.documents.find(
            (doc) => doc.id === documentId
          )

        if (!document) return

        setDocumentDetails({
          documentTitle: document.title,
          dataSource: `${selectedWorkspace.dataSource?.typeCode ?? ''}: ${
            selectedWorkspace.dataSource?.title ?? ''
          }`,
          latestIteration: latestIteration,
          status: document.documentSetStatus,
          lastUpdate: document.creationDate,
          ofrTemplateName:
            selectedWorkspace.config?.ofrDocumentTemplatesName ?? '',
          owner: findUser(document.ownerId ?? ''),
        })
      } finally {
        setIsLoading(false)
      }
    }

    fetchDocumentDetails()
  }, [selectedWorkspace, documentId, findUser, getLatestIterationOfFile])

  if (!selectedWorkspace || isLoading) {
    return (
      <SectionLoadingStateContainer>
        <SectionLoadingState />
      </SectionLoadingStateContainer>
    )
  }

  return (
    <Form layout="vertical" requiredMark={false}>
      <MultiColumnFormContentContainer>
        <MultiColumnFormColumnContainer>
          <ReadOnlyFormField
            label={t(
              'workspaceDetailsPage.workspaceOverviewInfoPanel.firstColumn.documentTitle'
            )}
          >
            {documentDetails?.documentTitle || NO_DATA_PLACEHOLDER}
          </ReadOnlyFormField>

          <ReadOnlyFormField
            label={t(
              'workspaceDetailsPage.workspaceOverviewInfoPanel.firstColumn.dataSource'
            )}
          >
            <Badge color={BadgeColors.DATA_SOURCE} icon={<DatabaseOutlined />}>
              {documentDetails?.dataSource || NO_DATA_PLACEHOLDER}
            </Badge>
          </ReadOnlyFormField>

          <ReadOnlyFormField
            label={t(
              'workspaceDetailsPage.workspaceOverviewInfoPanel.firstColumn.status'
            )}
          >
            <Badge
              color={
                documentDetails?.status === workspaceDocumentSetStatuses.OPENED
                  ? BadgeColors.STATUS_OPENED
                  : BadgeColors.STATUS_CLOSED
              }
            >
              {t(
                `workspaceDetailsPage.documentStatus.${documentDetails?.status}`
              )}
            </Badge>
          </ReadOnlyFormField>

          <ReadOnlyFormField
            label={t(
              'workspaceDetailsPage.workspaceOverviewInfoPanel.firstColumn.ofrTemplateName'
            )}
          >
            <Badge color={BadgeColors.ERROR} icon={<LayoutOutlined />}>
              {documentDetails?.ofrTemplateName || NO_DATA_PLACEHOLDER}
            </Badge>
          </ReadOnlyFormField>

          <ReadOnlyFormField
            label={t(
              'workspaceDetailsPage.workspaceOverviewInfoPanel.firstColumn.latestIteration'
            )}
          >
            <Badge color={BadgeColors.PROCESSING} icon={<FileTextOutlined />}>
              {documentDetails?.latestIteration || NO_DATA_PLACEHOLDER}
            </Badge>
          </ReadOnlyFormField>

          <ReadOnlyFormField
            label={t(
              'workspaceDetailsPage.workspaceOverviewInfoPanel.firstColumn.lastUpdate'
            )}
          >
            <IconTextContainer>
              <FieldTimeOutlined />
              {documentDetails?.lastUpdate ? (
                <Moment
                  date={documentDetails.lastUpdate}
                  format={dateFormat.PRIMARY}
                />
              ) : (
                NO_DATA_PLACEHOLDER
              )}
            </IconTextContainer>
          </ReadOnlyFormField>

          <ReadOnlyFormField
            label={t(
              'workspaceDetailsPage.workspaceOverviewInfoPanel.firstColumn.owner'
            )}
          >
            <IconTextContainer>
              <UserOutlined />
              {documentDetails?.owner?.displayName || NO_DATA_PLACEHOLDER}
            </IconTextContainer>
          </ReadOnlyFormField>
        </MultiColumnFormColumnContainer>
      </MultiColumnFormContentContainer>
    </Form>
  )
}

export default WorkspaceOverviewInfoConformityCheckDocumentPanel
